import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { SignOrderService } from '../services/sign-order.service';
import { OrderDetailResponse, StatusHistoryEntry } from '../models/sign-order.models';
import { formatSignOrderDateTime } from './sign-order-date.utils';
import { formatSignOrderWeight } from './sign-order-weight.utils';

@Component({
  selector: 'app-sign-order-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzAlertComponent,
    NzButtonModule,
    NzCardModule,
    NzDividerModule,
    NzIconModule,
    NzSpinModule,
    NzTableModule,
    NzTagModule,
    NzTimelineModule,
    TranslateModule,
  ],
  templateUrl: './sign-order-detail.component.html',
  styleUrls: ['./sign-order-detail.component.less'],
})
export class SignOrderDetailComponent implements OnInit, OnDestroy {
  readonly formatSignOrderDateTime = formatSignOrderDateTime;
  readonly formatSignOrderWeight = formatSignOrderWeight;

  @Input() publicId: string | null = null;
  @Input() embedded = false;
  @Output() navigateBack = new EventEmitter<void>();
  order: OrderDetailResponse | null = null;
  orderItems: any[] = [];
  isLoading = true;
  errorMessage: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private signOrderService: SignOrderService,
    public translate: TranslateService,
  ) {}

  ngOnInit(): void {
    const id = this.publicId || this.route.snapshot.paramMap.get('publicId');
    if (id) {
      this.loadOrder(id);
    } else {
      this.isLoading = false;
      this.errorMessage = this.translate.instant('SIGN_ORDER.DETAIL.INVALID_ID');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOrder(publicId: string): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.signOrderService.getOrder(publicId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (order) => {
          this.order = order;
          this.orderItems = [...(order.items || [])];
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          if (err.status === 404) {
            this.errorMessage = this.translate.instant('SIGN_ORDER.DETAIL.NOT_FOUND');
          } else {
            this.errorMessage = this.translate.instant('SIGN_ORDER.DETAIL.ERROR_LOADING');
          }
        },
      });
  }

  formatCents(cents: number, currency: string): string {
    const lang = this.translate.currentLang === 'de' ? 'de-DE' : 'en-US';
    const amount = (cents / 100).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${amount} ${currency}`;
  }

  formatVatRate(basisPoints: number): string {
    return `${(basisPoints / 100).toFixed(1)}%`;
  }

  localizedText(text: { de: string; en: string; [key: string]: string } | undefined): string {
    if (!text) return '';
    const lang = this.translate.currentLang || 'de';
    return text[lang] || text.de || text.en || '';
  }

  getStatusLabel(status: string): string {
    return this.translate.instant(`SIGN_ORDER.STATUS.${status.toUpperCase()}`);
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      pending_payment: 'orange',
      payment_received: 'blue',
      processing: 'processing',
      ordered_from_supplier: 'cyan',
      received_from_supplier: 'geekblue',
      shipped: 'purple',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  }

  getStatusHistory(): StatusHistoryEntry[] {
    if (!this.order?.statusHistory) return [];
    return this.order.statusHistory
      .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
  }
}
